import { useCallback, useMemo, useState } from 'react'
import {
  WbsItemStatus,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { WbsItemSearchConditionVO } from '../../../../domain/value-object/WbsItemSearchConditionVo'

const TaskActualWorkProxyRegistrationConditionHeaderFields = [
  'searchText',
  'rootUuid',
  'code',
  'types',
  'ticketTypes',
  'status',
  'priority',
  'displayName',
  'description',
  'teamUuid',
  'accountableUuid',
  'responsibleUuid',
  'assigneeUuid',
  'scheduledStartDate',
  'scheduledEndDate',
  'actualStartDate',
  'actualEndDate',
  'blank',
  'createdAt',
  'updatedAt',
  'createdByUuid',
  'updatedByUuid',
  'sprintUuid',
  'tagUuids',
] as const

export type TaskActualWorkProxyRegistrationCondition = Pick<
  WbsItemSearchConditionVO,
  (typeof TaskActualWorkProxyRegistrationConditionHeaderFields)[number]
>

const TaskActualWorkProxyRegistrationInitialCondition = {
  searchText: '',
  types: [WbsItemType.TASK],
}
const TaskActualWorkProxyRegistrationDefaultCondition = {
  searchText: '',
  types: [WbsItemType.TASK],
  status: [WbsItemStatus.TODO, WbsItemStatus.DOING, WbsItemStatus.REVIEW],
}
type UpdateTaskActualWorkProxyRegistrationCondition = (
  updated: Partial<TaskActualWorkProxyRegistrationCondition>
) => void

type TaskActualWorkProxyRegistrationConditionUseCases = {
  updateSearchCondition: UpdateTaskActualWorkProxyRegistrationCondition
  changeSelectedMember: (selectedUserUuid: string) => void
  overrideSearchCondition: (
    newSearchCondition: WbsItemSearchConditionVO
  ) => void
  resetSearchCondition: () => void
}

export const useTaskActualWorkProxyRegistrationCondition = (): {
  searchCondition: WbsItemSearchConditionVO
} & TaskActualWorkProxyRegistrationConditionUseCases => {
  const [searchCondition, setSearchCondition] =
    useState<TaskActualWorkProxyRegistrationCondition>(
      TaskActualWorkProxyRegistrationInitialCondition
    )

  const changeSelectedMember = useCallback((selectedUserUuid: string) => {
    setSearchCondition({
      ...TaskActualWorkProxyRegistrationDefaultCondition,
      responsibleUuid: selectedUserUuid,
    })
  }, [])

  const updateSearchCondition = useCallback(
    (updated: Partial<TaskActualWorkProxyRegistrationCondition>) => {
      setSearchCondition(prev => ({
        ...prev,
        ...updated,
      }))
    },
    []
  )

  const overrideSearchCondition = useCallback(
    (newSearchCondition: WbsItemSearchConditionVO) => {
      setSearchCondition(newSearchCondition)
    },
    []
  )

  const resetSearchCondition = useCallback(() => {
    setSearchCondition(TaskActualWorkProxyRegistrationDefaultCondition)
  }, [])

  const returnVal = useMemo(
    () => ({
      searchCondition,
      updateSearchCondition,
      changeSelectedMember,
      overrideSearchCondition,
      resetSearchCondition,
    }),
    [
      searchCondition,
      updateSearchCondition,
      changeSelectedMember,
      overrideSearchCondition,
      resetSearchCondition,
    ]
  )

  return returnVal
}
